import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { STATIC_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Lock, Download, Music, Check, ArrowLeft, CreditCard, FileText } from "lucide-react";

const STATUS_LABEL = (s) => s;

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [packageInfo, setPackageInfo] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      // also fetch package for pricing
      const pkgs = await api.get("/packages");
      setPackageInfo(pkgs.data.find((p) => p.key === data.package_key));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const toggle = (vid) =>
    setSelected((s) => (s.includes(vid) ? s.filter((x) => x !== vid) : [...s, vid]));

  const checkout = async () => {
    if (selected.length === 0) return;
    setCheckoutBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        order_id: order.id,
        variation_ids: selected,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setCheckoutBusy(false);
    }
  };

  const download = async (vid, title) => {
    try {
      const res = await api.get(`/orders/${id}/variations/${vid}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <div className="container-x py-20 text-white/60">Loading…</div>;
  if (!order) return <div className="container-x py-20 text-destructive">{error || "Order not found"}</div>;

  const perVarPrice = packageInfo ? packageInfo.price / Math.max(1, packageInfo.variations) : 0;
  const selectedTotal = selected.length * perVarPrice;

  return (
    <div className="bg-black min-h-[calc(100vh-80px)]">
      <div className="container-x py-10 max-w-5xl">
        <Link to="/dashboard" className="text-white/60 hover:text-gold text-sm inline-flex items-center gap-2 mb-6" data-testid="back-to-dashboard">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        <div className="bg-ink-900 border border-ink-700 p-6 mb-8">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
            <div>
              <div className="eyebrow mb-1">{order.package_name}</div>
              <h1 className="font-heading text-2xl md:text-3xl">{order.questionnaire?.company_name}</h1>
              <div className="text-white/60 text-sm mt-1">{order.questionnaire?.industry}</div>
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold border border-gold/40 text-gold px-3 py-1.5">
              {STATUS_LABEL(order.status)}
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-5 text-sm border-t border-ink-700 pt-4 mt-4">
            <div>
              <div className="eyebrow mb-1">Variations Promised</div>
              <div className="text-white">{order.variations_promised}</div>
            </div>
            <div>
              <div className="eyebrow mb-1">Variations Delivered</div>
              <div className="text-white">{order.variations?.length || 0}</div>
            </div>
            <div>
              <div className="eyebrow mb-1">Estimated Total</div>
              <div className="text-gold font-heading text-xl">${order.estimated_total?.toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* Brief summary */}
        <details className="bg-ink-900 border border-ink-700 mb-8 group" data-testid="brief-summary">
          <summary className="cursor-pointer p-5 text-sm tracking-[0.18em] uppercase text-gold font-semibold flex items-center gap-2">
            <FileText size={14} /> Creative Brief
          </summary>
          <div className="px-5 pb-5 grid sm:grid-cols-2 gap-4 text-sm">
            <BriefItem label="Brand Message" value={order.questionnaire?.brand_message} />
            <BriefItem label="Target Audience" value={order.questionnaire?.target_audience} />
            <BriefItem label="Music Style" value={order.questionnaire?.music_style} />
            <BriefItem label="Voice Preferences" value={order.questionnaire?.voice_preferences} />
            <BriefItem label="Competitors" value={order.questionnaire?.competitors} />
            <BriefItem label="References" value={order.questionnaire?.song_references} />
            <BriefItem label="Notes" value={order.questionnaire?.notes} />
          </div>
        </details>

        {/* Variations */}
        <h2 className="font-heading text-2xl mb-5">Song Variations</h2>
        {(!order.variations || order.variations.length === 0) ? (
          <div className="bg-ink-900 border border-ink-700 p-10 text-center" data-testid="no-variations">
            <Music className="text-gold mx-auto mb-4" size={36} />
            <h3 className="font-heading text-xl mb-2">Your project is in production</h3>
            <p className="text-white/60">Once Songniture completes your variations, you&apos;ll be able to preview and purchase them here.</p>
          </div>
        ) : (
          <>
            <div className="space-y-5 mb-8">
              {order.variations.map((v) => {
                const purchased = order.purchased_variation_ids?.includes(v.id);
                const isSelected = selected.includes(v.id);
                let borderClass = "border-ink-700";
                if (purchased) borderClass = "border-green-500/40";
                else if (isSelected) borderClass = "border-gold";
                return (
                  <div key={v.id} className={`bg-ink-900 border p-6 transition ${borderClass}`} data-testid={`variation-${v.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <h3 className="font-heading text-xl mb-2">{v.title}</h3>
                        {v.description && <p className="text-white/65 text-sm mb-3">{v.description}</p>}
                        {v.creative_notes && <div className="text-white/55 text-xs mb-1"><span className="text-gold">Creative:</span> {v.creative_notes}</div>}
                        {v.marketing_purpose && <div className="text-white/55 text-xs"><span className="text-gold">Use:</span> {v.marketing_purpose}</div>}
                      </div>
                      <div className="text-right">
                        {purchased ? (
                          <div className="inline-flex items-center gap-2 text-green-400 text-sm font-semibold">
                            <Check size={16} /> Purchased
                          </div>
                        ) : (
                          <div className="text-gold font-heading text-2xl">${perVarPrice.toFixed(0)}</div>
                        )}
                      </div>
                    </div>
                    <div className="mt-4">
                      <audio controls preload="none" src={`${STATIC_BASE}${v.audio_url}`} data-testid={`variation-audio-${v.id}`} />
                    </div>
                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                      {purchased ? (
                        <button onClick={() => download(v.id, v.title)} className="btn-gold" data-testid={`download-${v.id}`}>
                          <Download size={14} /> Download
                        </button>
                      ) : (
                        <button
                          onClick={() => toggle(v.id)}
                          className={isSelected ? "btn-gold" : "btn-outline"}
                          data-testid={`select-${v.id}`}
                        >
                          {isSelected ? <><Check size={14} /> Selected</> : <><Lock size={14} /> Select to Unlock</>}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Checkout bar */}
            {selected.length > 0 && (
              <div className="sticky bottom-4 z-20" data-testid="checkout-bar">
                <div className="bg-ink-900 border-2 border-gold p-5 flex items-center justify-between gap-4 flex-wrap shadow-gold-strong">
                  <div>
                    <div className="text-white/60 text-sm">{selected.length} variation{selected.length > 1 ? "s" : ""} selected</div>
                    <div className="font-heading text-2xl text-gold">${selectedTotal.toFixed(2)}</div>
                    {order.addons_total > 0 && (
                      <div className="text-white/50 text-xs">+ ${order.addons_total.toFixed(2)} add-ons</div>
                    )}
                  </div>
                  <button onClick={checkout} disabled={checkoutBusy} className="btn-gold" data-testid="checkout-button">
                    <CreditCard size={16} /> {checkoutBusy ? "Redirecting…" : "Pay & Unlock"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {error && <div className="mt-6 text-destructive" data-testid="order-detail-error">{error}</div>}
      </div>
    </div>
  );
}

function BriefItem({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-gold font-semibold mb-1">{label}</div>
      <div className="text-white/80">{value}</div>
    </div>
  );
}
